/**
 * Constants for AI Fashion / AI Models prompt generation.
 * Kept in sync with iOS `develop` branch (AIModelsManager.swift, AIModelsPoseManager.swift).
 */

/**
 * Dynamic prompt `requirements` list, ported from iOS `AIModelsManager.defaultRequirements`.
 * Styling and product-placement lines vary with whether the subject is a minor and
 * whether the product is a wearable item.
 */
export function buildDefaultRequirements(
  options: { isMinorSubject?: boolean; isWearable?: boolean; isMannequinSubject?: boolean } = {}
): string[] {
  const isMinorSubject = options.isMinorSubject ?? false
  const isWearable = options.isWearable ?? true
  const isMannequinSubject = options.isMannequinSubject ?? false

  const stylingRequirement = isMinorSubject
    ? "Keep styling age-appropriate, child-safe, and natural for the subject's age, the product, and its intended audience. Avoid editorial, adult-coded, mature, sensual, or fashion-model presentation."
    : 'Use contemporary styling that suits the product. Do not add layers that cover the product. Avoid distracting garments and visible undergarments. For luxury, beauty, jewelry, fragrance, formal, or editorial products, use polished, refined styling. For swimwear or underwear products on clearly adult subjects (18+), do not add extra clothes for coverage.'
  const productPlacementRequirement = isWearable
    ? 'The product must be worn on the subject in its intended body location.'
    : 'The product must be presented naturally, whether held, carried, placed, or positioned.'

  if (isMannequinSubject) {
    return [
      'If the reference shows a clearly artificial, abstract display mannequin, preserve its original surface and add no garments beyond the displayed products. For humanlike mannequins, complete a coherent, fully dressed outfit with complementary opaque garments where needed, including bottoms when displaying a top. Preserve the mannequin\u2019s material and form.',
      productPlacementRequirement,
    ]
  }

  const requirements = [
    'Create a newly styled, fully dressed look for the subject.',
    stylingRequirement,
    productPlacementRequirement,
  ]

  if (isWearable) {
    requirements.push(
      isMinorSubject
        ? 'Add opaque clothing if needed to ensure full coverage.'
        : 'If the product does not cover visible intimate areas, add complementary opaque garments.'
    )
  }
  requirements.push('No nudity or implied nudity. Never show uncovered chest, buttocks, groin, or upper thighs.')
  return requirements
}

/** Whether the model catalog marks the selected template as a mannequin. */
export function isMannequinSubjectGender(gender: string | null | undefined): boolean {
  return gender?.trim().toLowerCase() === 'mannequin'
}

/**
 * Extra per-panel requirements appended to {@link buildDefaultRequirements} for
 * the pose-grid variants. Ported from iOS `AIModelsPoseManager.buildGridPrompt`.
 */
export const GRID_VARIANT_EXTRA_REQUIREMENTS: string[] = [
  "Match this panel's composition exactly. Do not crop tighter than requested. If full body is requested, show the entire subject from head to toe within the frame, including both feet when applicable.",
  'In this panel, include the primary product and any additional items from the product reference images whenever the requested crop allows those items to be visible. Do not omit referenced items that should be visible in the frame.',
  'Do not render the primary product alone. It must remain visibly presented by the subject within the frame, worn if wearable and otherwise held, carried, or positioned naturally.',
]

/**
 * Grid-level consistency requirements. Ported from iOS
 * `AIModelsPoseManager.buildGridPrompt` (top-level `requirements`).
 */
export const GRID_TOP_LEVEL_REQUIREMENTS: string[] = [
  "Keep the model's outfit and styling consistent across all panels unless explicitly changed.",
  'Preserve the same target aspect ratio and e-commerce product focus in every panel.',
]

/**
 * Body-type → prompt description catalog, ported from iOS
 * `AIModelBodyTypePromptCatalog`. Lookup is case-insensitive on the trimmed value;
 * unknown values pass through unchanged.
 */
export const AI_MODEL_BODY_TYPE_CATALOG: Record<string, string> = {
  slim: 'slim build, narrow frame, lean limbs, low body fat',
  average: 'average build, balanced proportions, medium frame, natural everyday body shape',
  athletic: 'athletic build, visibly toned body, defined arms and legs, strong fit proportions',
  'full-figured': 'full-figured build, fuller hips and bust, soft waist definition, fuller proportions than average',
  'plus-size': 'visibly plus-size build, full figure, broader hips, rounder midsection, thicker arms and legs, visibly larger than full-figured',
}

/** Maps a raw body-type value to its catalog prompt description; falls back to the trimmed value. */
export function resolveBodyTypeDescription(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  return AI_MODEL_BODY_TYPE_CATALOG[trimmed.toLowerCase()] ?? trimmed
}

/**
 * Normalizes a subject age string, ported from iOS `normalizedSubjectAge`.
 * `newborn` → `newborn baby`; empty/nullish → undefined; otherwise the trimmed value.
 */
export function normalizedSubjectAge(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  if (trimmed.toLowerCase() === 'newborn') return 'newborn baby'
  return trimmed
}

const MINOR_AGE_KEYWORDS = new Set(['newborn', 'newborn baby', 'baby', 'toddler'])
const AGE_RANGE_REGEX = /(\d+)\s*(?:to|[-–])\s*(\d+)/i

/**
 * Determines whether a subject age denotes a minor, ported from iOS
 * `AIModelsManager.isMinorSubjectAge`. Note: per iOS, `child`/`preteen` are NOT
 * flagged as minor here (they still emit `subject.age`); only baby/toddler/newborn
 * keywords or a numeric/range value under 18 flip the requirement variant.
 */
export function isMinorSubjectAge(age: string | null | undefined): boolean {
  const trimmed = age?.trim() ?? ''
  const lowercased = trimmed.toLowerCase()
  if (MINOR_AGE_KEYWORDS.has(lowercased)) return true

  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10) < 18
  }

  const rangeMatch = AGE_RANGE_REGEX.exec(trimmed)
  if (rangeMatch) {
    return parseInt(rangeMatch[1], 10) < 18
  }

  const numericTokens = trimmed.split(/\D+/).filter(t => t.length > 0)
  if (numericTokens.length === 1) {
    return parseInt(numericTokens[0], 10) < 18
  }

  return false
}

/**
 * Human-readable aspect ratio labels used in AI prompt text so the model knows
 * what framing each panel (and therefore the full grid) should use.
 */
export const ASPECT_RATIO_LABELS: Record<string, string> = {
  square: '1:1 (square)',
  portrait: '3:4 (portrait)',
  portrait_4_5: '4:5 (portrait)',
  portrait_2_3: '2:3 (portrait)',
  portrait_9_16: '9:16 (portrait)',
}

const ASPECT_RATIO_NUMERIC: Record<string, number> = {
  square: 1,
  portrait: 3 / 4,
  portrait_4_5: 4 / 5,
  portrait_2_3: 2 / 3,
  portrait_9_16: 9 / 16,
}

/** Closest catalog label for a numeric width/height ratio; falls back to a 2-decimal string. */
export function layoutAspectRatioLabel(aspectRatio: number): string {
  let bestKey: string | null = null
  let bestDiff = Infinity
  for (const [key, numeric] of Object.entries(ASPECT_RATIO_NUMERIC)) {
    const diff = Math.abs(numeric - aspectRatio)
    if (diff < bestDiff) {
      bestDiff = diff
      bestKey = key
    }
  }
  if (bestKey && bestDiff < 0.02 && ASPECT_RATIO_LABELS[bestKey]) {
    return ASPECT_RATIO_LABELS[bestKey]
  }
  return aspectRatio.toFixed(2)
}

const DEFAULT_INSTRUCTION_SUFFIX = 'using standard fashion styling.'

/**
 * Builds the instruction string that tells Gemini which image is the product
 * and which is the model reference — matching iOS `AIModelsPromptMetadata.defaultInstruction`.
 */
export function buildDefaultInstruction(
  productImageCount: number,
  modelImageIndex: number | null,
  productImageStartIndex: number = 1
): string {
  const count = Math.max(productImageCount, 1)
  if (modelImageIndex == null) {
    return `Use image ${productImageStartIndex} as the primary product reference ${DEFAULT_INSTRUCTION_SUFFIX}`
  }
  if (count === 1) {
    return `Use image ${productImageStartIndex} as the primary product reference and image ${modelImageIndex} as the model reference ${DEFAULT_INSTRUCTION_SUFFIX}`
  }
  const additionalStart = productImageStartIndex + 1
  const ref = additionalStart === modelImageIndex - 1
    ? `image ${additionalStart}`
    : `images ${Array.from({ length: modelImageIndex - additionalStart }, (_, i) => String(additionalStart + i)).join(', ')}`
  return `Use image ${productImageStartIndex} as the primary product reference. Use ${ref} as additional product references for other views or complementary items. Use image ${modelImageIndex} as the model reference ${DEFAULT_INSTRUCTION_SUFFIX}`
}

export const HI_RES_COMPOSITION_PROMPT =
  'Upscale the scene in the first image, dramatically increasing the resolution and clarity. ' +
  'Base the high-definition features and attributes of the product and model on images 2 and 3, respectively. ' +
  'However, you must replicate EXACTLY the geometry, structure, expression, and absolute composition of all elements as seen in the first image. ' +
  'The high-definition source material must ONLY be used to apply flawless, detailed textures and patterns to that precise structure, with zero modification to the composition.'
